import React, { useState, useRef } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'sonner';
import { useActivityLogger } from './GamificationComponents';
import { PaymentButton, PaymentSecurityBadge, ProductDownloads } from './PaymentComponents';

// Shadcn UI Components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';

// Icons
import { 
  Dumbbell, Plus, Trash2, DollarSign, Clock, Activity, Target, Flame, Timer,
  ShoppingCart, Lock, Unlock, Star, Video, Upload, Play, Download, ChevronDown,
  ChevronUp, Eye, X, Loader2, FileVideo, CheckCircle2, Camera, Bookmark
} from 'lucide-react';

import { BACKEND_URL, API } from './config';

// Fitness niche options for plan creation
const PLAN_NICHES = [
  { id: 'bodybuilding', label: 'Bodybuilding', color: '#DC2626' },
  { id: 'powerlifting', label: 'Powerlifting', color: '#EA580C' },
  { id: 'strongman', label: 'Strongman', color: '#D97706' },
  { id: 'hyrox', label: 'HYROX', color: '#65A30D' },
  { id: 'crossfit', label: 'CrossFit / Functional', color: '#059669' },
  { id: 'yoga', label: 'Yoga', color: '#0891B2' },
  { id: 'pilates', label: 'Pilates', color: '#7C3AED' },
  { id: 'calisthenics', label: 'Calisthenics', color: '#2563EB' },
  { id: 'hiit', label: 'HIIT / Cardio', color: '#E11D48' },
  { id: 'muscle_building', label: 'Muscle Building', color: '#B91C1C' },
  { id: 'weight_loss', label: 'Weight Loss', color: '#16A34A' },
  { id: 'endurance', label: 'Endurance / Running', color: '#0D9488' },
  { id: 'flexibility', label: 'Flexibility / Mobility', color: '#8B5CF6' },
  { id: 'sports_specific', label: 'Sport-Specific Training', color: '#0284C7' },
  { id: 'rehab', label: 'Rehab / Prehab', color: '#6366F1' },
  { id: 'martial_arts', label: 'Martial Arts / Combat', color: '#DC2626' },
  { id: 'swimming', label: 'Swimming', color: '#0EA5E9' },
  { id: 'cycling', label: 'Cycling', color: '#F59E0B' },
];

// Niche selector chip UI for plan creation/editing
const NicheSelector = ({ selected = [], onChange, label = 'Fitness Niches' }) => (
  <div className="space-y-2">
    <label className="text-sm font-medium">{label}</label>
    <div className="flex flex-wrap gap-1.5">
      {PLAN_NICHES.map(n => {
        const active = selected.includes(n.id);
        return (
          <button
            key={n.id}
            type="button"
            onClick={() => onChange(active ? selected.filter(x => x !== n.id) : [...selected, n.id])}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
              active ? 'text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            style={active ? { backgroundColor: n.color } : {}}
            data-testid={`niche-chip-${n.id}`}
          >
            {n.label}
          </button>
        );
      })}
    </div>
    {selected.length > 0 && (
      <p className="text-xs text-gray-500">{selected.length} selected</p>
    )}
  </div>
);

// Media URL resolver (same as App.js)
const resolveMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return BACKEND_URL + url;
  return url;
};

// =============================================================================
// VIDEO UPLOAD COMPONENT (reusable per-exercise)
// =============================================================================
const VideoUploadField = ({ value, onChange, label = "Demo Video", exerciseIndex }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video must be under 50MB');
      return;
    }

    setUploading(true);
    setProgress(0);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const sessionToken = Cookies.get('session_token');
      const resp = await axios.post(`${API}/upload/media?category=exercise-video`, formData, {
        headers: { 
          Authorization: `Bearer ${sessionToken}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
      onChange(resp.data.url);
      toast.success('Video uploaded');
    } catch (err) {
      console.error('Video upload failed:', err);
      toast.error(err.response?.data?.detail || 'Video upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-gray-500">{label}</label>
      <input ref={inputRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/x-msvideo" className="hidden" onChange={handleUpload} data-testid={`exercise-video-input-${exerciseIndex}`} />
      
      {value ? (
        <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
          <span className="text-xs text-emerald-700 truncate flex-1">Video attached</span>
          <button onClick={() => onChange(null)} className="text-gray-400 hover:text-red-500 transition-colors">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : uploading ? (
        <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
          <Loader2 className="h-4 w-4 text-blue-600 animate-spin shrink-0" />
          <div className="flex-1">
            <div className="h-1.5 bg-blue-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <span className="text-xs text-blue-600">{progress}%</span>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full flex items-center gap-2 p-2 border border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50/50 transition-all text-left"
          data-testid={`exercise-video-upload-${exerciseIndex}`}
        >
          <FileVideo className="h-4 w-4 text-gray-400" />
          <span className="text-xs text-gray-500">Add demo video (MP4, MOV &middot; max 50MB)</span>
        </button>
      )}
    </div>
  );
};

const ExercisePhotoUpload = ({ value, onChange, exerciseIndex }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Photo must be under 10MB'); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const sessionToken = Cookies.get('session_token');
      const resp = await axios.post(`${API}/upload/media?category=exercise-video`, formData, {
        headers: { Authorization: `Bearer ${sessionToken}`, 'Content-Type': 'multipart/form-data' }
      });
      onChange(resp.data.url);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally { setUploading(false); }
  };

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-gray-500">Exercise Photo (optional)</label>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleUpload} data-testid={`exercise-photo-input-${exerciseIndex}`} />
      {value ? (
        <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
          <CheckCircle2 className="h-4 w-4 text-blue-600 shrink-0" />
          <span className="text-xs text-blue-700 truncate flex-1">Photo attached</span>
          <button onClick={() => onChange(null)} className="text-gray-400 hover:text-red-500 transition-colors"><X className="h-3.5 w-3.5" /></button>
        </div>
      ) : uploading ? (
        <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
          <Loader2 className="h-4 w-4 text-blue-600 animate-spin shrink-0" />
          <span className="text-xs text-blue-600">Uploading...</span>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} className="w-full flex items-center gap-2 p-2 border border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50/50 transition-all text-left" data-testid={`exercise-photo-upload-${exerciseIndex}`}>
          <Camera className="h-4 w-4 text-gray-400" />
          <span className="text-xs text-gray-500">Add exercise photo (JPEG, PNG &middot; max 10MB)</span>
        </button>
      )}
    </div>
  );
};



// =============================================================================
// CREATE WORKOUT PLAN DIALOG
// =============================================================================
export const CreateWorkoutPlanDialog = ({ open, onClose, onSuccess, editPlan = null }) => {
  const { logActivity } = useActivityLogger();

  const buildFormData = (plan) => {
    if (!plan) return {
      title: '', description: '', price: '', audience: 'public', pricing: 'one_time_purchase',
      difficulty_level: 'beginner', duration_weeks: 4, equipment_needed: [], muscle_groups_targeted: [],
      calories_burned_estimate: '', video_description_url: '', fitness_niches: [],
      exercises: [{ name: '', sets: 3, reps: '10-12', rest_time: '60 seconds', instructions: '', muscle_groups: [], demo_video_url: null, photo_url: null }]
    };
    return {
      title: plan.title || '',
      description: plan.description || '',
      price: plan.price ? String(plan.price) : '',
      audience: plan.audience || 'public',
      pricing: plan.pricing || (plan.price > 0 ? 'one_time_purchase' : 'free'),
      difficulty_level: plan.difficulty_level || 'beginner',
      duration_weeks: plan.duration_weeks || 4,
      equipment_needed: plan.equipment_needed || [],
      muscle_groups_targeted: plan.muscle_groups_targeted || [],
      calories_burned_estimate: plan.calories_burned_estimate ? String(plan.calories_burned_estimate) : '',
      video_description_url: plan.video_description_url || '',
      fitness_niches: plan.fitness_niches || [],
      exercises: (plan.exercises && plan.exercises.length > 0) ? plan.exercises.map(e => ({
        name: e.name || '', sets: e.sets || 3, reps: e.reps || '10-12', rest_time: e.rest_time || '60 seconds',
        instructions: e.instructions || '', muscle_groups: e.muscle_groups || [],
        demo_video_url: e.demo_video_url || null, photo_url: e.photo_url || null
      })) : [{ name: '', sets: 3, reps: '10-12', rest_time: '60 seconds', instructions: '', muscle_groups: [], demo_video_url: null, photo_url: null }]
    };
  };

  const [formData, setFormData] = useState(buildFormData(null));
  const [loading, setLoading] = useState(false);
  const [overviewVideoUploading, setOverviewVideoUploading] = useState(false);
  const overviewVideoRef = useRef(null);
  const isEditMode = !!editPlan;

  // Preload when editPlan changes
  React.useEffect(() => {
    if (open) setFormData(buildFormData(editPlan));
  }, [open, editPlan]);

  const addExercise = () => {
    setFormData({
      ...formData,
      exercises: [...formData.exercises, { name: '', sets: 3, reps: '10-12', rest_time: '60 seconds', instructions: '', muscle_groups: [], demo_video_url: null, photo_url: null }]
    });
  };

  const removeExercise = (index) => {
    setFormData({ ...formData, exercises: formData.exercises.filter((_, i) => i !== index) });
  };

  const updateExercise = (index, field, value) => {
    const newExercises = [...formData.exercises];
    newExercises[index] = { ...newExercises[index], [field]: value };
    setFormData({ ...formData, exercises: newExercises });
  };

  const handleOverviewVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { toast.error('Video must be under 50MB'); return; }
    setOverviewVideoUploading(true);
    try {
      const sessionToken = Cookies.get('session_token');
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${API}/upload/media?category=exercise-video`, fd, {
        headers: { Authorization: `Bearer ${sessionToken}`, 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, video_description_url: resp.data.url }));
      toast.success('Overview video uploaded');
    } catch (err) {
      toast.error('Overview video upload failed');
    } finally {
      setOverviewVideoUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Please fill in title and description');
      return;
    }
    if (formData.pricing === 'one_time_purchase' && (!formData.price || parseFloat(formData.price) <= 0)) {
      toast.error('Please set a price for one-time purchase products');
      return;
    }
    setLoading(true);
    try {
      const sessionToken = Cookies.get('session_token');
      const submitData = {
        ...formData,
        price: formData.pricing === 'free' ? 0 : parseFloat(formData.price),
        calories_burned_estimate: formData.calories_burned_estimate ? parseInt(formData.calories_burned_estimate) : null,
        equipment_needed: formData.equipment_needed.filter(item => item.trim()),
        muscle_groups_targeted: formData.muscle_groups_targeted.filter(item => item.trim())
      };
      if (isEditMode) {
        await axios.put(`${API}/workout-plans/${editPlan.id}`, submitData, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        toast.success('Workout plan updated!');
      } else {
        await axios.post(`${API}/workout-plans`, submitData, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        await logActivity('workout_completed', `Created workout plan: ${formData.title}`);
        toast.success('Workout plan created successfully!');
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} workout plan:`, error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} workout plan`);
    } finally {
      setLoading(false);
    }
  };

  // Helper to describe the current access combo
  const getAccessSummary = () => {
    const { audience, pricing } = formData;
    if (audience === 'public' && pricing === 'free') return 'Anyone can access this plan for free.';
    if (audience === 'public' && pricing === 'one_time_purchase') return 'Anyone can buy this plan with a one-time payment.';
    if (audience === 'subscribers' && pricing === 'free') return 'Only your subscribers can access this plan (included with subscription).';
    if (audience === 'subscribers' && pricing === 'one_time_purchase') return 'Only subscribers can buy this plan — subscription + one-time payment required.';
    return '';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" onKeyDown={(e) => {
        const tag = e.target?.tagName;
        if ((tag === 'INPUT' || tag === 'TEXTAREA') && e.key === ' ') e.stopPropagation();
      }}>
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Dumbbell className="h-5 w-5 text-blue-600" />
            <span>{isEditMode ? 'Edit Workout Plan' : 'Create Workout Plan'}</span>
          </DialogTitle>
          <DialogDescription>
            {isEditMode ? 'Update your workout plan details and exercises' : 'Create a premium workout plan with per-exercise demo videos'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Plan Title *</label>
            <Input placeholder="e.g., 6-Week Muscle Building Program" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} data-testid="workout-title-input" />
          </div>

          {/* Audience + Pricing — 2 selectors side by side */}
          <div className="p-4 rounded-xl border-2 border-blue-100 bg-blue-50/30 space-y-3">
            <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Lock className="h-4 w-4 text-blue-600" />
              Access &amp; Pricing
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-600">Who can access?</label>
                <Select value={formData.audience} onValueChange={(value) => setFormData({ ...formData, audience: value })} data-testid="workout-audience-select">
                  <SelectTrigger data-testid="workout-audience-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public — Everyone</SelectItem>
                    <SelectItem value="subscribers">Subscribers Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-600">How do they pay?</label>
                <Select value={formData.pricing} onValueChange={(value) => setFormData({ ...formData, pricing: value })} data-testid="workout-pricing-select">
                  <SelectTrigger data-testid="workout-pricing-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="one_time_purchase">One-Time Purchase</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {formData.pricing === 'one_time_purchase' && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-600">Price ($) *</label>
                <Input type="number" placeholder="29.99" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} data-testid="workout-price-input" className="max-w-[200px]" />
              </div>
            )}
            <p className="text-xs text-blue-700 bg-blue-100/50 rounded-lg px-3 py-2" data-testid="workout-access-summary">{getAccessSummary()}</p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Description *</label>
            <Textarea placeholder="Describe your workout plan, who it's for, and what results they can expect..." value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} data-testid="workout-description-input" />
          </div>

          <NicheSelector
            selected={formData.fitness_niches}
            onChange={(v) => setFormData({ ...formData, fitness_niches: v })}
            label="Fitness Niches (helps discovery)"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Difficulty Level</label>
              <Select value={formData.difficulty_level} onValueChange={(value) => setFormData({ ...formData, difficulty_level: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Duration (weeks)</label>
              <Input type="number" value={formData.duration_weeks} onChange={(e) => setFormData({ ...formData, duration_weeks: parseInt(e.target.value) || 1 })} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Est. Calories Burned</label>
              <Input type="number" placeholder="300" value={formData.calories_burned_estimate} onChange={(e) => setFormData({ ...formData, calories_burned_estimate: e.target.value })} />
            </div>
          </div>

          {/* Overview Video */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Plan Overview Video (Optional)</label>
            <input ref={overviewVideoRef} type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={handleOverviewVideoUpload} />
            {formData.video_description_url ? (
              <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <span className="text-sm text-emerald-700 flex-1">Overview video attached</span>
                <button onClick={() => setFormData(prev => ({ ...prev, video_description_url: '' }))} className="text-gray-400 hover:text-red-500"><X className="h-4 w-4" /></button>
              </div>
            ) : overviewVideoUploading ? (
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                <span className="text-sm text-blue-600">Uploading...</span>
              </div>
            ) : (
              <button type="button" onClick={() => overviewVideoRef.current?.click()} className="w-full flex items-center gap-3 p-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50/40 transition-all" data-testid="workout-overview-video-upload">
                <Video className="h-6 w-6 text-gray-400" />
                <div className="text-left">
                  <span className="text-sm text-gray-600 block">Upload a plan overview/intro video</span>
                  <span className="text-xs text-gray-400">MP4, MOV, WebM up to 50MB</span>
                </div>
              </button>
            )}
            <p className="text-xs text-gray-500">A general intro video for the whole plan. Individual exercises can have their own demo videos below.</p>
          </div>

          {/* Equipment and Muscle Groups */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Equipment Needed</label>
              <Input placeholder="Dumbbells, Barbell, Bench (comma separated)" value={formData.equipment_needed.join(', ')} onChange={(e) => { const parts = e.target.value.split(','); setFormData({ ...formData, equipment_needed: parts.map((item, i) => i < parts.length - 1 ? item.trim() : item) }); }} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Muscle Groups Targeted</label>
              <Input placeholder="Chest, Back, Legs (comma separated)" value={formData.muscle_groups_targeted.join(', ')} onChange={(e) => { const parts = e.target.value.split(','); setFormData({ ...formData, muscle_groups_targeted: parts.map((item, i) => i < parts.length - 1 ? item.trim() : item) }); }} />
            </div>
          </div>

          {/* Exercises */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Exercises</h3>
              <Button onClick={addExercise} size="sm" variant="outline" data-testid="add-exercise-btn">
                <Plus className="h-4 w-4 mr-1" />
                Add Exercise
              </Button>
            </div>

            <div className="space-y-4">
              {formData.exercises.map((exercise, index) => (
                <Card key={index} className="border-blue-100">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm text-gray-900">Exercise {index + 1}</h4>
                      {formData.exercises.length > 1 && (
                        <Button onClick={() => removeExercise(index)} size="sm" variant="ghost" className="text-red-500 hover:text-red-600 h-7 w-7 p-0">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Input placeholder="Exercise name (e.g., Push-ups)" value={exercise.name} onChange={(e) => updateExercise(index, 'name', e.target.value)} data-testid={`exercise-name-${index}`} />
                    <div className="grid grid-cols-3 gap-2">
                      <Input type="number" placeholder="Sets" value={exercise.sets} onChange={(e) => updateExercise(index, 'sets', parseInt(e.target.value) || 1)} />
                      <Input placeholder="Reps (e.g., 10-12)" value={exercise.reps} onChange={(e) => updateExercise(index, 'reps', e.target.value)} />
                      <Input placeholder="Rest (e.g., 60s)" value={exercise.rest_time} onChange={(e) => updateExercise(index, 'rest_time', e.target.value)} />
                    </div>
                    <Textarea placeholder="Exercise instructions and form tips..." value={exercise.instructions} onChange={(e) => updateExercise(index, 'instructions', e.target.value)} rows={2} />
                    <Input placeholder="Muscle groups (comma separated)" value={exercise.muscle_groups.join(', ')} onChange={(e) => { const parts = e.target.value.split(','); updateExercise(index, 'muscle_groups', parts.map((item, i) => i < parts.length - 1 ? item.trim() : item)); }} />
                    
                    {/* Per-exercise video upload */}
                    <VideoUploadField
                      value={exercise.demo_video_url}
                      onChange={(url) => updateExercise(index, 'demo_video_url', url)}
                      label="Exercise Demo Video"
                      exerciseIndex={index}
                    />
                    {/* Per-exercise photo upload */}
                    <ExercisePhotoUpload
                      value={exercise.photo_url}
                      onChange={(url) => updateExercise(index, 'photo_url', url)}
                      exerciseIndex={index}
                    />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading} className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white" data-testid="create-workout-submit">
              {loading ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Workout Plan')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// =============================================================================
// EXERCISE VIDEO PLAYER (inline expandable)
// =============================================================================
const ExerciseVideoPlayer = ({ url, exerciseName }) => {
  const [playing, setPlaying] = useState(false);
  const fullUrl = resolveMediaUrl(url);
  if (!fullUrl) return null;

  return (
    <div className="mt-3">
      {!playing ? (
        <button
          onClick={() => setPlaying(true)}
          className="flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors w-full text-left group"
          data-testid={`play-video-${exerciseName}`}
        >
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center group-hover:bg-blue-700 transition-colors shrink-0">
            <Play className="h-4 w-4 text-white ml-0.5" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-blue-700 block truncate">Watch Demo</span>
            <span className="text-xs text-blue-500">Tap to play exercise demonstration</span>
          </div>
        </button>
      ) : (
        <div className="rounded-lg overflow-hidden border border-gray-200 bg-black">
          <video
            src={fullUrl}
            controls
            autoPlay
            className="w-full max-h-[300px]"
            style={{ objectFit: 'contain' }}
            data-testid={`video-player-${exerciseName}`}
          />
          <div className="flex items-center justify-between px-3 py-2 bg-gray-900">
            <button onClick={() => setPlaying(false)} className="text-xs text-gray-400 hover:text-white transition-colors">
              Hide video
            </button>
            <a href={fullUrl} download className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors" data-testid={`download-video-${exerciseName}`}>
              <Download className="h-3 w-3" />
              Download
            </a>
          </div>
        </div>
      )}
    </div>
  );
};


// =============================================================================
// WORKOUT PLAN DETAIL VIEW (used in both tabs and profile)
// =============================================================================
export const WorkoutPlanDetailView = ({ plan, open, onClose }) => {
  const [expandedExercise, setExpandedExercise] = useState(null);

  // Reset state when dialog closes so reopening starts clean
  React.useEffect(() => {
    if (!open) setExpandedExercise(null);
  }, [open]);

  if (!plan) return null;

  const overviewVideoUrl = resolveMediaUrl(plan.video_description_url);
  const exercisesWithVideo = (plan.exercises || []).filter(e => e.demo_video_url);
  const exercisesWithPhoto = (plan.exercises || []).filter(e => e.photo_url);
  const { audience, pricing } = getAccessModel(plan);

  const getDifficultyStyle = (level) => {
    switch (level) {
      case 'beginner': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'intermediate': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'advanced': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0" data-testid="workout-detail-view">
        {/* Hero header */}
        <div className="bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white p-6 pb-8 rounded-t-lg">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-3 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`${getDifficultyStyle(plan.difficulty_level)} text-xs font-semibold px-2.5 py-0.5 border`}>
                  {plan.difficulty_level?.toUpperCase()}
                </Badge>
                {pricing === 'one_time_purchase' && (
                  <Badge className="bg-blue-500/20 text-blue-200 border-blue-400/30 border text-xs px-2.5 py-0.5">
                    <DollarSign className="h-3 w-3 mr-0.5" />${plan.price}
                  </Badge>
                )}
                {pricing === 'free' && (
                  <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/30 border text-xs px-2.5 py-0.5">Free</Badge>
                )}
                {audience === 'subscribers' && (
                  <Badge className="bg-amber-500/20 text-amber-200 border-amber-400/30 border text-xs px-2.5 py-0.5">
                    <Lock className="h-3 w-3 mr-0.5" />Subscribers
                  </Badge>
                )}
              </div>
              <h2 className="text-2xl font-bold leading-tight" data-testid="workout-detail-title">{plan.title}</h2>
              <p className="text-sm text-blue-200/80">by {plan.creator_name}</p>
            </div>
            <Dumbbell className="h-10 w-10 text-blue-400/40 shrink-0" />
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mt-6">
            <div className="bg-white/10 rounded-lg p-3 text-center backdrop-blur-sm">
              <Clock className="h-5 w-5 text-blue-300 mx-auto mb-1" />
              <div className="text-lg font-bold">{plan.duration_weeks}</div>
              <div className="text-xs text-blue-300/70">Weeks</div>
            </div>
            <div className="bg-white/10 rounded-lg p-3 text-center backdrop-blur-sm">
              <Activity className="h-5 w-5 text-orange-300 mx-auto mb-1" />
              <div className="text-lg font-bold">{(plan.exercises || []).length}</div>
              <div className="text-xs text-blue-300/70">Exercises</div>
            </div>
            {exercisesWithVideo.length > 0 && (
              <div className="bg-white/10 rounded-lg p-3 text-center backdrop-blur-sm">
                <Video className="h-5 w-5 text-emerald-300 mx-auto mb-1" />
                <div className="text-lg font-bold">{exercisesWithVideo.length}</div>
                <div className="text-xs text-blue-300/70">Videos</div>
              </div>
            )}
            {plan.calories_burned_estimate && (
              <div className="bg-white/10 rounded-lg p-3 text-center backdrop-blur-sm">
                <Flame className="h-5 w-5 text-red-300 mx-auto mb-1" />
                <div className="text-lg font-bold">{plan.calories_burned_estimate}</div>
                <div className="text-xs text-blue-300/70">Calories</div>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Description */}
          {plan.description && (
            <div>
              <p className="text-gray-700 leading-relaxed" data-testid="workout-detail-description">{plan.description}</p>
            </div>
          )}

          {/* Overview Video */}
          {overviewVideoUrl && (
            <div className="rounded-xl overflow-hidden border border-gray-200 bg-black">
              <video src={overviewVideoUrl} controls className="w-full max-h-[350px]" style={{ objectFit: 'contain' }} data-testid="workout-overview-video" />
              <div className="flex items-center justify-between px-3 py-2 bg-gray-900">
                <span className="text-xs text-gray-400">Plan Overview Video</span>
                <a href={overviewVideoUrl} download className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300">
                  <Download className="h-3 w-3" /> Download
                </a>
              </div>
            </div>
          )}

          {/* Equipment & Muscle Groups */}
          <div className="grid md:grid-cols-2 gap-4">
            {(plan.equipment_needed || []).length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-900">Equipment Needed</h4>
                <div className="flex flex-wrap gap-1.5">
                  {plan.equipment_needed.map((eq, i) => (
                    <Badge key={i} variant="outline" className="text-xs border-gray-300">{eq}</Badge>
                  ))}
                </div>
              </div>
            )}
            {(plan.muscle_groups_targeted || []).length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-900">Muscle Groups</h4>
                <div className="flex flex-wrap gap-1.5">
                  {plan.muscle_groups_targeted.map((mg, i) => (
                    <Badge key={i} className="bg-blue-50 text-blue-700 border-blue-200 border text-xs">{mg}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Exercises List */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-600" />
              Exercises ({(plan.exercises || []).length})
            </h4>
            
            <div className="space-y-2">
              {(plan.exercises || []).map((exercise, idx) => {
                const isExpanded = expandedExercise === idx;
                return (
                  <div
                    key={idx}
                    className={`border rounded-xl overflow-hidden transition-all duration-200 ${isExpanded ? 'border-blue-300 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                    data-testid={`exercise-detail-${idx}`}
                  >
                    {/* Exercise header - always visible */}
                    <button
                      onClick={() => setExpandedExercise(isExpanded ? null : idx)}
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50/50 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold shrink-0">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{exercise.name || `Exercise ${idx + 1}`}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {exercise.sets} sets &times; {exercise.reps} reps &middot; Rest {exercise.rest_time}
                          {exercise.demo_video_url && <span className="text-blue-500 ml-2"><Video className="h-3 w-3 inline" /> Video</span>}
                          {exercise.photo_url && <span className="text-emerald-500 ml-2"><Camera className="h-3 w-3 inline" /> Photo</span>}
                        </div>
                      </div>
                      {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                    </button>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
                        {/* Stats pills */}
                        <div className="flex flex-wrap gap-2">
                          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-full">
                            <Target className="h-3.5 w-3.5 text-blue-600" />
                            <span className="text-xs font-medium text-blue-700">{exercise.sets} sets</span>
                          </div>
                          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 rounded-full">
                            <Activity className="h-3.5 w-3.5 text-orange-600" />
                            <span className="text-xs font-medium text-orange-700">{exercise.reps} reps</span>
                          </div>
                          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 rounded-full">
                            <Timer className="h-3.5 w-3.5 text-violet-600" />
                            <span className="text-xs font-medium text-violet-700">Rest {exercise.rest_time}</span>
                          </div>
                        </div>

                        {/* Instructions */}
                        {exercise.instructions && (
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Instructions</div>
                            <p className="text-sm text-gray-700 leading-relaxed">{exercise.instructions}</p>
                          </div>
                        )}

                        {/* Muscle groups */}
                        {(exercise.muscle_groups || []).length > 0 && exercise.muscle_groups[0] !== '' && (
                          <div className="flex flex-wrap gap-1.5">
                            {exercise.muscle_groups.filter(Boolean).map((mg, i) => (
                              <Badge key={i} variant="outline" className="text-xs border-gray-300">{mg}</Badge>
                            ))}
                          </div>
                        )}

                        {/* Demo Video */}
                        {exercise.demo_video_url && (
                          <ExerciseVideoPlayer url={exercise.demo_video_url} exerciseName={exercise.name} />
                        )}

                        {/* Exercise Photo */}
                        {exercise.photo_url && (
                          <div className="rounded-xl overflow-hidden border border-gray-200 cursor-pointer group relative" style={{ maxHeight: exercise.demo_video_url ? '12rem' : '20rem' }} data-testid={`exercise-photo-view-${idx}`}>
                            <img src={resolveMediaUrl(exercise.photo_url)} alt={exercise.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" />
                            <div className="absolute bottom-2 left-2">
                              <Badge className="bg-black/50 text-white border-0 text-xs backdrop-blur-sm"><Camera className="h-3 w-3 mr-1" />Exercise Photo</Badge>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Product Downloads (if applicable) */}
          <ProductDownloads productType="workout_plan" productId={plan.id} />
        </div>
      </DialogContent>
    </Dialog>
  );
};


// Helper: derive audience+pricing from plan data (handles both new and old records)
const getAccessModel = (plan) => {
  if (plan.audience && plan.pricing) return { audience: plan.audience, pricing: plan.pricing };
  // Backward compat from old access_type
  const at = plan.access_type || '';
  const price = plan.price || 0;
  if (at === 'purchase') return { audience: 'public', pricing: 'one_time_purchase' };
  if (at === 'subscription') return price > 0 ? { audience: 'subscribers', pricing: 'one_time_purchase' } : { audience: 'subscribers', pricing: 'free' };
  if (at === 'public') return price > 0 ? { audience: 'public', pricing: 'one_time_purchase' } : { audience: 'public', pricing: 'free' };
  return price > 0 ? { audience: 'public', pricing: 'one_time_purchase' } : { audience: 'public', pricing: 'free' };
};

// =============================================================================
// WORKOUT PLANS TAB (marketplace / dashboard)
// =============================================================================
// ── Bookmark Button ────────────────────────────────────────────────────────
const BookmarkBtn = ({ productId, productType, className = '' }) => {
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API}/bookmarks/status/${productType}/${productId}`);
        setSaved(res.data.saved);
      } catch {}
    };
    check();
  }, [productId, productType]);

  const toggle = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      if (saved) {
        await axios.post(`${API}/bookmarks/remove`, { product_id: productId, product_type: productType });
        setSaved(false);
        toast.success('Removed from saved');
      } else {
        await axios.post(`${API}/bookmarks/save`, { product_id: productId, product_type: productType });
        setSaved(true);
        toast.success('Saved to My Library');
      }
    } catch { toast.error('Failed to update bookmark'); }
    setBusy(false);
  };

  return (
    <button
      onClick={toggle}
      disabled={busy}
      className={`p-1.5 rounded-full transition-colors ${saved ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' : 'text-gray-400 hover:text-blue-500 hover:bg-gray-100'} ${className}`}
      data-testid={`bookmark-workout-${productId}`}
      title={saved ? 'Remove from saved' : 'Save to My Workouts'}
    >
      <Bookmark className={`h-4 w-4 ${saved ? 'fill-blue-600' : ''}`} />
    </button>
  );
};

export const WorkoutPlansTab = ({ workoutPlans }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);

  const getDifficultyColor = (level) => {
    switch (level) {
      case 'beginner': return 'bg-green-100 text-green-700';
      case 'intermediate': return 'bg-yellow-100 text-yellow-700';
      case 'advanced': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (workoutPlans.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Dumbbell className="h-8 w-8 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No workout plans available</h3>
        <p className="text-gray-600">Check back later for new workout plans from creators!</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {workoutPlans.map((plan) => {
          const isLocked = plan.is_locked;
          const { audience, pricing } = getAccessModel(plan);
          const lockRequires = plan.lock_requires || '';
          const description = plan.description || plan.description_preview || '';

          return (
            <Card key={plan.id} className={`group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${isLocked ? 'border-gray-200 opacity-95' : 'border-blue-100 cursor-pointer'}`}
              onClick={() => !isLocked && setSelectedPlan(plan)}
              data-testid={`workout-card-${plan.id}`}
            >
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-2 min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge className={`${getDifficultyColor(plan.difficulty_level)} px-2 py-1`}>
                        <Target className="h-3 w-3 mr-1" />
                        {plan.difficulty_level}
                      </Badge>
                      {audience === 'subscribers' && (
                        <Badge data-testid={`workout-audience-badge-${plan.id}`} className="bg-amber-100 text-amber-700 px-2 py-1">
                          <Lock className="h-3 w-3 mr-1" />Subscribers
                        </Badge>
                      )}
                      {pricing === 'one_time_purchase' && (
                        <Badge data-testid={`workout-pricing-badge-${plan.id}`} className="bg-blue-100 text-blue-700 px-2 py-1">
                          <DollarSign className="h-3 w-3 mr-1" />${plan.price}
                        </Badge>
                      )}
                      {!isLocked && (audience !== 'public' || pricing !== 'free') && (
                        <Badge className="bg-emerald-100 text-emerald-700 px-2 py-1">
                          <Unlock className="h-3 w-3 mr-1" />Unlocked
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-lg line-clamp-2">{plan.title}</CardTitle>
                    {plan.creator_name && (
                      <p className="text-xs text-gray-500 mt-0.5">by {plan.creator_name}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0 flex flex-col items-end gap-1">
                    <BookmarkBtn productId={plan.id} productType="workout" />
                    {pricing === 'one_time_purchase' && <div className="text-xl font-bold text-blue-600 whitespace-nowrap">${plan.price}</div>}
                    {pricing === 'free' && <div className="text-base font-bold text-green-600">Free</div>}
                    <div className="text-xs text-gray-500 whitespace-nowrap">{plan.purchases_count || 0} sales</div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <div className="flex items-center space-x-1"><Clock className="h-4 w-4" /><span>{plan.duration_weeks} weeks</span></div>
                  {plan.calories_burned_estimate && (
                    <div className="flex items-center space-x-1"><Flame className="h-4 w-4" /><span>{plan.calories_burned_estimate} cal</span></div>
                  )}
                </div>
              </CardHeader>

              <CardContent>
                <div className="space-y-4">
                  <p className="text-gray-700 text-sm line-clamp-3">{description}</p>

                  {!isLocked && plan.equipment_needed && plan.equipment_needed.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-gray-900">Equipment:</div>
                      <div className="flex flex-wrap gap-1">
                        {(plan.equipment_needed || []).slice(0, 3).map((eq, i) => <Badge key={i} variant="outline" className="text-xs">{eq}</Badge>)}
                        {(plan.equipment_needed || []).length > 3 && <Badge variant="outline" className="text-xs">+{plan.equipment_needed.length - 3}</Badge>}
                      </div>
                    </div>
                  )}

                  {!isLocked && plan.exercises && (
                    <div className="pt-2 border-t flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-1 text-gray-600">
                        <Activity className="h-4 w-4" /><span>{(plan.exercises || []).length} exercises</span>
                        {(plan.exercises || []).some(e => e.demo_video_url) && (
                          <span className="text-blue-500 ml-2 flex items-center gap-0.5"><Video className="h-3 w-3" /> Videos</span>
                        )}
                        {(plan.exercises || []).some(e => e.photo_url) && (
                          <span className="text-emerald-500 ml-2 flex items-center gap-0.5"><Camera className="h-3 w-3" /> Photos</span>
                        )}
                      </div>
                      <div className="text-xs text-blue-500 font-medium">View Plan &rarr;</div>
                    </div>
                  )}

                  {isLocked && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 border border-gray-200">
                      <Lock className="h-4 w-4 text-gray-400 shrink-0" />
                      <span className="text-sm text-gray-500">{plan.lock_message || 'Full details locked'}</span>
                    </div>
                  )}

                  {/* CTAs for locked plans */}
                  {isLocked && lockRequires === 'subscription' && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Button data-testid={`workout-subscribe-cta-${plan.id}`} className="w-full bg-amber-500 hover:bg-amber-600 text-white" onClick={() => window.location.href = `/profile/${plan.creator_id}`}>
                        <Lock className="h-4 w-4 mr-2" />Subscribe to Access
                      </Button>
                    </div>
                  )}
                  {isLocked && lockRequires === 'purchase' && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <PaymentButton data-testid={`workout-buy-cta-${plan.id}`} itemType="workout_plan" itemId={plan.id} price={plan.price} title={plan.title} description={description} className="w-full">
                        Buy ${plan.price}
                      </PaymentButton>
                    </div>
                  )}
                  {isLocked && lockRequires === 'subscription_and_purchase' && (
                    <div onClick={(e) => e.stopPropagation()} className="space-y-2">
                      <Button data-testid={`workout-subscribe-cta-${plan.id}`} className="w-full bg-amber-500 hover:bg-amber-600 text-white" onClick={() => window.location.href = `/profile/${plan.creator_id}`}>
                        <Lock className="h-4 w-4 mr-2" />Subscribe + Buy ${plan.price}
                      </Button>
                      <p className="text-xs text-center text-gray-500">Requires active subscription and one-time purchase</p>
                    </div>
                  )}
                  {!isLocked && audience === 'public' && pricing === 'free' && (
                    <div className="text-xs text-center text-green-600 font-medium py-1">
                      <Unlock className="h-3 w-3 inline mr-1" />Free Access — Click to view
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detail View Dialog */}
      <WorkoutPlanDetailView plan={selectedPlan} open={!!selectedPlan} onClose={() => setSelectedPlan(null)} />
    </>
  );
};


// =============================================================================
// MY WORKOUT PLANS TAB (purchased + saved + subscription-access)
// =============================================================================
export const MyWorkoutPlansTab = ({ myWorkoutPlans }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);

  const getDifficultyColor = (level) => {
    switch (level) {
      case 'beginner': return 'bg-green-100 text-green-700';
      case 'intermediate': return 'bg-yellow-100 text-yellow-700';
      case 'advanced': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (myWorkoutPlans.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Bookmark className="h-8 w-8 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No workout plans yet</h3>
        <p className="text-gray-600">Save or purchase workout plans to build your library!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {myWorkoutPlans.map((plan) => {
          const isAccessible = plan.is_accessible !== false;
          const isLocked = plan.is_locked || !isAccessible;
          const isPurchased = plan.is_purchased;
          const isSaved = plan.is_saved;
          const hasSub = plan.has_subscription_access;
          const description = plan.description || plan.description_preview || '';

          return (
            <Card
              key={plan.id}
              className={`group hover:shadow-lg transition-all duration-300 ${isLocked ? 'border-gray-200 opacity-90' : 'border-green-100 cursor-pointer'}`}
              onClick={() => isAccessible && setSelectedPlan(plan)}
              data-testid={`my-workout-card-${plan.id}`}
            >
              <CardHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {isPurchased && (
                      <Badge className="bg-green-100 text-green-700 px-2 py-1">
                        <Unlock className="h-3 w-3 mr-1" />Purchased
                      </Badge>
                    )}
                    {isSaved && (
                      <Badge className="bg-blue-100 text-blue-700 px-2 py-1">
                        <Bookmark className="h-3 w-3 mr-1 fill-blue-700" />Saved
                      </Badge>
                    )}
                    {hasSub && !isPurchased && isAccessible && (
                      <Badge className="bg-purple-100 text-purple-700 px-2 py-1">
                        <Star className="h-3 w-3 mr-1" />Subscription
                      </Badge>
                    )}
                    {isLocked && (
                      <Badge className="bg-gray-100 text-gray-500 px-2 py-1">
                        <Lock className="h-3 w-3 mr-1" />Locked
                      </Badge>
                    )}
                  </div>
                  {plan.difficulty_level && (
                    <Badge className={`${getDifficultyColor(plan.difficulty_level)} px-2 py-1 capitalize`}>
                      {plan.difficulty_level}
                    </Badge>
                  )}
                </div>
                <CardTitle className="text-lg line-clamp-2">{plan.title}</CardTitle>
                {plan.creator_name && (
                  <p className="text-xs text-gray-500">by {plan.creator_name}</p>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-gray-700 text-sm line-clamp-2">{description}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    {plan.duration_weeks && (
                      <div className="flex items-center space-x-1"><Clock className="h-4 w-4" /><span>{plan.duration_weeks} weeks</span></div>
                    )}
                    {plan.exercises && (
                      <div className="flex items-center space-x-1"><Activity className="h-4 w-4" /><span>{(plan.exercises || []).length} exercises</span></div>
                    )}
                  </div>

                  {isLocked && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                      <Lock className="h-4 w-4 text-amber-500 shrink-0" />
                      <span className="text-sm text-amber-700">
                        {plan.lock_message || 'Subscribe to unlock this content'}
                      </span>
                    </div>
                  )}

                  {isLocked && plan.creator_id && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Button
                        className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                        onClick={() => window.location.href = `/profile/${plan.creator_id}`}
                        data-testid={`my-workout-subscribe-cta-${plan.id}`}
                      >
                        <Lock className="h-4 w-4 mr-2" />Subscribe to Unlock
                      </Button>
                    </div>
                  )}

                  {isAccessible && (
                    <div className="text-xs text-center text-blue-500 font-medium pt-1">
                      Click to view full plan &rarr;
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detail View Dialog */}
      <WorkoutPlanDetailView plan={selectedPlan} open={!!selectedPlan} onClose={() => setSelectedPlan(null)} />
    </div>
  );
};
